import { useEffect, useMemo } from 'react';
import { ArrowDown } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useTasks } from '../stores/tasks.js';
import { useProjects } from '../stores/projects.js';
import { useUi } from '../stores/ui.js';
import { TaskRow } from '../components/TaskRow.js';
import { EmptyState } from '../components/EmptyState.js';
import { ListSkeleton } from '../components/TaskList.js';
import { ProjectHeading } from '../components/ProjectHeading.js';
import { ActionButtons } from '../components/ActionButtons.js';
import { InstallPromptCard } from '../components/InstallPromptCard.js';
import { formatLong, isoWeekNumber, todayInParis } from '../lib/dates.js';
import { PageHeader } from '../components/PageHeader.js';
import { agendaTasks } from '../lib/agenda-filter.js';
import { usePullToRefresh } from '../hooks/usePullToRefresh.js';
import { SEUIL } from '../lib/pull-to-refresh.js';
import { brancherRetourPremierPlan } from '../lib/premier-plan.js';
import { useMessages } from '../stores/messages.js';
import { useToast } from '../stores/toast.js';
import { drain } from '../lib/offline/replay.js';
import { OfflineError } from '../api/client.js';
import { errorMessage } from '../lib/errors.js';
import type { Task } from '../api/tasks.js';
import type { Project } from '../api/projects.js';

interface ProjectGroup {
  project: Project;
  items: Task[];
}

// Un projet = une carte (Braise 2) : les groupes se lisent comme des objets posés sur la page,
// plus comme une seule longue liste où seuls des petits caps séparaient les projets.
function ProjectSection({ group }: { group: ProjectGroup }): React.ReactElement {
  return (
    <section className="carte carte-serree">
      <ProjectHeading project={group.project} count={group.items.length} className="mb-1 px-3" />
      <div className="liste">
        {group.items.map((t) => (
          <TaskRow key={t.taskId} task={t} hideProject showSubtitle hideToday />
        ))}
      </div>
    </section>
  );
}

function groupByProject(items: Task[], projects: Project[]): ProjectGroup[] {
  const byProject = new Map<string, Task[]>();
  for (const t of items) {
    const arr = byProject.get(t.projectId) ?? [];
    arr.push(t);
    byProject.set(t.projectId, arr);
  }
  return projects
    .map((p) => ({ project: p, items: byProject.get(p.projectId) ?? [] }))
    .filter((g) => g.items.length > 0);
}

/**
 * Ce que tirer pour rafraîchir relit : tout ce que la page affiche — les tâches du jour, les
 * projets qui les groupent et les teintent, le compte de la cloche. Au réseau, jamais au cache
 * (`FRAIS`). Une file hors-ligne non vide part d'abord : la relire par-dessus effacerait ce
 * qu'elle porte (LEARNINGS, règle de l'état local autoritaire).
 */
async function relireToday(): Promise<void> {
  await drain(); // rend la main tout de suite quand la file est vide
  await Promise.all([useTasks.getState().refresh('today'), useProjects.getState().refresh()]);
}

async function rafraichirToday(): Promise<void> {
  try {
    await Promise.all([relireToday(), useMessages.getState().load()]);
  } catch (err) {
    useToast.getState().show({
      message:
        err instanceof OfflineError ? 'Offline — showing the last loaded list' : errorMessage(err),
    });
  }
}

/**
 * L'indicateur du geste : une pastille qui descend avec le doigt, sa flèche se retourne passé
 * le seuil (« relâchez »), puis la roue du kit tant que les appels sont en vol. Il pousse la
 * page au lieu de la recouvrir, comme Todoist.
 */
function IndicateurTirage(): React.ReactElement | null {
  // Le geste vit ici et non dans Today : chaque `touchmove` ne re-rend que la pastille.
  const { distance, refreshing } = usePullToRefresh(rafraichirToday);
  if (!refreshing && distance === 0) return null;
  const hauteur = refreshing ? SEUIL : distance;
  const pret = distance >= SEUIL;
  return (
    <div
      className="flex items-end justify-center overflow-hidden"
      style={{ height: hauteur }}
      data-testid="pull-indicator"
    >
      <div className="mb-3 flex size-9 items-center justify-center rounded-full border border-edge bg-surface-raised" style={{ boxShadow: 'var(--ombre-legere)' }}>
        {refreshing ? (
          <span className="chargement" role="status" aria-label="Refreshing" />
        ) : (
          <ArrowDown
            size={18}
            aria-hidden
            className="text-muted transition-transform"
            style={{ transform: pret ? 'rotate(180deg)' : undefined, opacity: Math.min(1, distance / SEUIL) }}
          />
        )}
      </div>
    </div>
  );
}

export function Today(): React.ReactElement {
  const { tasks, loading, load } = useTasks();
  const projects = useProjects((s) => s.projects);
  const openQuickAdd = useUi((s) => s.openQuickAdd);
  const openTaskDetail = useUi((s) => s.openTaskDetail);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    load('today');
  }, [load]);

  // Le retour au premier plan relit la liste, en silence (pas de toast hors-ligne : personne
  // n'a rien demandé) ; la cloche est relue par App au même moment. Pas sous une modale ouverte :
  // TaskDetailModal reconstruit son brouillon quand la tâche du store change, l'édition en cours
  // (aller copier un lien dans une autre app, revenir) serait perdue.
  useEffect(
    () =>
      brancherRetourPremierPlan(
        document,
        relireToday,
        () => document.querySelector('dialog[open]') !== null
      ),
    []
  );

  // `?q` ouvre le quick-add, `?task=<id>` le détail d'une tâche : ce que les widgets demandent
  // par l'URL. La page applique puis retire le paramètre.
  useEffect(() => {
    const task = searchParams.get('task');
    if (!searchParams.has('q') && task === null) return;
    if (searchParams.has('q')) openQuickAdd();
    if (task) openTaskDetail(task);
    const next = new URLSearchParams(searchParams);
    next.delete('q');
    next.delete('task');
    setSearchParams(next, { replace: true });
  }, [searchParams, openQuickAdd, openTaskDetail, setSearchParams]);

  const { overdueGroups, todayGroups, overdueCount, visibleCount } = useMemo(() => {
    const today = todayInParis();
    const visible = agendaTasks(tasks, projects, today);
    const overdue = visible.filter((t) => t.dueDate !== null && t.dueDate < today);
    const rest = visible.filter((t) => !(t.dueDate !== null && t.dueDate < today));
    return {
      overdueGroups: groupByProject(overdue, projects),
      todayGroups: groupByProject(rest, projects),
      overdueCount: overdue.length,
      visibleCount: visible.length,
    };
  }, [tasks, projects]);

  const today = todayInParis();
  const surtitre = (
    <>
      {formatLong(today)} · week {isoWeekNumber(today)}
      {!loading && visibleCount > 0 && ` · ${visibleCount} to do`}
    </>
  );

  return (
    <>
      <IndicateurTirage />
      <div className="max-w-2xl mx-auto w-full px-4 md:px-6 py-5 md:py-8 space-y-5">
        <PageHeader title="Today" surtitre={surtitre} actions={<ActionButtons />} />
        <div className="md:hidden">
          <InstallPromptCard />
        </div>

        {loading ? (
          <ListSkeleton />
        ) : visibleCount === 0 ? (
          <EmptyState>Nothing due today — enjoy the silence.</EmptyState>
        ) : (
          <div className="space-y-4">
            {overdueCount > 0 && (
              // Le retard se dit par son titre, en encre d'erreur, et par sa place en tête — ni
              // surface rouge sous des lignes qu'on doit encore lire, ni filet à gauche (Braise 2).
              <section className="space-y-2">
                <h2 className="mention px-3 text-error">Overdue · {overdueCount}</h2>
                {overdueGroups.map((g) => (
                  <ProjectSection key={g.project.projectId} group={g} />
                ))}
              </section>
            )}
            {todayGroups.map((g) => (
              <ProjectSection key={g.project.projectId} group={g} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}
